import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config/api";
import {
  Paper,
  makeStyles,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Select,
  MenuItem,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  TextField,
  Button,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  ExitToApp as ExitToAppIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";

const drawerWidth = "10%";
const BASE_URL = API_BASE_URL;
const STORAGE_KEY = "selectedKWListValue";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    overflowX: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  formContainer: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    borderRadius: theme.spacing(1),
    width: "90%",
  },
  select: {
    marginBottom: theme.spacing(2),
  },
  selectText: {
    fontSize: "1.5rem",
    marginBottom: theme.spacing(2),
  },
  tableHeadRow: {
    height: 64,
  },
  tableHeadCell: {
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
}));

const LIST_OPTIONS = [
  { value: "kwPowodyWpisania", label: "Lista powodów wpisania" },
  { value: "kwRodzajSad", label: "Rodzaj SAD" },
  { value: "kwProcedura", label: "Procedura" },
  { value: "kwRodzajBledu", label: "Rodzaj błędu" },
];

const KWLists = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newRecordName, setNewRecordName] = useState("");
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "kwPowodyWpisania",
  );

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const handleSelectChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    localStorage.setItem(STORAGE_KEY, value);
    setTableData([]);
    setSearchTerm("");
    setSelectedRow(null);
    setIsAddingRecord(false);
    setNewRecordName("");
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow || !selectedValue) {
      return;
    }

    try {
      await axios.delete(
        `${BASE_URL}/delete/${selectedValue}/${selectedRow.id}`,
        {
          headers: getAuthHeaders(),
        },
      );

      await fetchData();
      toast.success(`Poprawnie usunięto ${selectedRow.name}!`, {
        autoClose: 3000,
      });
      setSelectedRow(null);
    } catch (error) {
      console.error("Error deleting row:", error);
      toast.error("Wystąpił błąd podczas usuwania.", {
        autoClose: 3000,
      });
    }
  };

  const handleBackToTableClick = () => {
    localStorage.removeItem(STORAGE_KEY);
    navigate("/kontrolaWewnetrzna");
  };

  const handleLogoutClick = () => {
    localStorage.clear();
    navigate("/login");
  };

  const checkDuplicateRecord = (nameToCheck) => {
    const normalizedName = nameToCheck.trim().toLowerCase();
    return tableData.some((row) => row.name.toLowerCase() === normalizedName);
  };

  const handleSaveRecordClick = async () => {
    if (!selectedValue) {
      toast.error("Wybierz listę.", {
        autoClose: 3000,
      });
      return;
    }

    if (!newRecordName.trim()) {
      toast.error("Nazwa nie może być pusta!", {
        autoClose: 3000,
      });
      return;
    }

    if (checkDuplicateRecord(newRecordName)) {
      toast.error("Rekord o takiej nazwie już istnieje w liście!", {
        autoClose: 3000,
      });
      return;
    }

    const formattedName =
      selectedValue === "kwPowodyWpisania" || selectedValue === "kwRodzajBledu"
        ? newRecordName.trim()
        : newRecordName.trim().toUpperCase();

    try {
      await axios.post(
        `${BASE_URL}/add-record/${selectedValue}`,
        { name: formattedName },
        { headers: getAuthHeaders() },
      );

      await fetchData();
      setNewRecordName("");
      setIsAddingRecord(false);

      toast.success("Poprawnie dodano nowy rekord!", {
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error adding new record:", error);
      toast.error("Wystąpił błąd podczas dodawania rekordu!", {
        autoClose: 3000,
      });
    }
  };

  const fetchData = useCallback(async () => {
    if (!selectedValue) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}/list/${selectedValue}`, {
        headers: getAuthHeaders(),
      });
      setTableData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Nie udało się pobrać danych listy.", {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedTableData = [...tableData].sort((a, b) =>
    a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
  );

  const filteredTableData = sortedTableData.filter((row) =>
    row.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className={classes.root}>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{ paper: classes.drawerPaper }}
      >
        <Toolbar />
        <List>
          <ListItem button onClick={handleBackToTableClick}>
            <ListItemIcon>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Powrót do KW" />
          </ListItem>
          <ListItem button onClick={handleLogoutClick}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Wyloguj" />
          </ListItem>
        </List>
      </Drawer>

      <main className={classes.content}>
        <Paper elevation={3} className={classes.formContainer}>
          <Typography className={classes.selectText}>
            Zarządzanie listami KW
          </Typography>

          <Select
            className={classes.select}
            label="Wybierz listę"
            value={selectedValue}
            onChange={handleSelectChange}
            variant="outlined"
            fullWidth
          >
            {LIST_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {loading && <CircularProgress style={{ margin: "20px" }} />}

          {!loading && (
            <>
              <TextField
                label="Wyszukaj po nazwie"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ marginBottom: "20px" }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsAddingRecord(true)}
                style={{ marginBottom: "20px" }}
              >
                Dodaj nową pozycję do tabeli
              </Button>

              {isAddingRecord && (
                <>
                  <TextField
                    label="Wpisz nazwę"
                    variant="outlined"
                    fullWidth
                    value={newRecordName}
                    onChange={(event) => setNewRecordName(event.target.value)}
                    style={{ marginBottom: "20px" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveRecordClick}
                    style={{ marginRight: "10px" }}
                  >
                    Zapisz
                  </Button>
                  <Button
                    variant="contained"
                    color="default"
                    onClick={() => {
                      setNewRecordName("");
                      setIsAddingRecord(false);
                    }}
                  >
                    Zamknij
                  </Button>
                </>
              )}

              <TableContainer component={Paper} style={{ marginTop: "20px" }}>
                <Table>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell className={classes.tableHeadCell}>
                        Nr.
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Nazwa
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Usuń
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTableData.length > 0 ? (
                      filteredTableData.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>
                            {selectedRow && selectedRow.id === row.id ? (
                              <IconButton onClick={handleConfirmDelete}>
                                <DeleteIcon color="secondary" />
                              </IconButton>
                            ) : (
                              <IconButton
                                onClick={() => handleDeleteClick(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Brak danych do wyświetlenia.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      </main>
      <ToastContainer position="top-right" />
    </div>
  );
};

export default KWLists;
